import type { Logger, LogLevel } from './types';

const LOG_LEVEL_MAP: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  none: 4,
};

/**
 * 分级控制台日志实现
 */
export class DefaultLogger implements Logger {
  private levelValue: number;

  constructor(level: LogLevel = 'warn') {
    this.levelValue = LOG_LEVEL_MAP[level] ?? LOG_LEVEL_MAP.warn;
  }

  public setLevel(level: LogLevel): void {
    this.levelValue = LOG_LEVEL_MAP[level] ?? LOG_LEVEL_MAP.warn;
  }

  public debug(...args: any[]): void {
    if (this.levelValue <= LOG_LEVEL_MAP.debug) {
      console.debug('[HSS-TRACK:DEBUG]', ...args);
    }
  }

  public info(...args: any[]): void {
    if (this.levelValue <= LOG_LEVEL_MAP.info) {
      console.info('[HSS-TRACK:INFO]', ...args);
    }
  }

  public warn(...args: any[]): void {
    if (this.levelValue <= LOG_LEVEL_MAP.warn) {
      console.warn('[HSS-TRACK:WARN]', ...args);
    }
  }

  public error(...args: any[]): void {
    if (this.levelValue <= LOG_LEVEL_MAP.error) {
      console.error('[HSS-TRACK:ERROR]', ...args);
    }
  }
}

/**
 * 安全执行外壳（Safe Shell）
 * 阻断所有未捕获异常外溢，保护主业务流程
 */
export class SafeShell {
  constructor(private logger: Logger) {}

  /**
   * 安全执行同步函数，发生错误时记录日志并返回兜底值
   */
  public run<T>(fn: () => T, fallback: T, actionName = 'Action'): T {
    try {
      const result = fn();
      if (result && typeof (result as any).then === 'function') {
        return Promise.resolve(result).catch(() => {
          this.report(actionName);
          return fallback;
        }) as T;
      }
      return result;
    } catch (error) {
      this.report(actionName);
      return fallback;
    }
  }

  /**
   * 安全执行异步函数，发生错误时记录日志并返回兜底值
   */
  public async runAsync<T>(fn: () => Promise<T>, fallback: T, actionName = 'AsyncAction'): Promise<T> {
    try {
      return await fn();
    } catch (error) {
      this.report(actionName);
      return fallback;
    }
  }

  private report(actionName: string): void {
    try {
      // 宿主错误对象可能携带原始业务数据，日志只记录固定操作名称。
      this.logger.error(`[SafeShell] ${actionName} 执行异常已拦截`);
    } catch {
      // 日志设施自身失败也不能逃出安全边界。
    }
  }
}
